/**
 * Frontend service for user preferences (saved list views).
 *
 * Saved views are server-persisted on the user document via
 * `/users/me/saved-views/:listKey`. This module is the thin client wrapper —
 * it knows nothing about <EntityList> internals.
 */
import type { EmailNotificationPreferences, SavedView, SavedViewList, ThemeId } from '@atlas/shared';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';

let csrfTokenRef: { value: string | null } | null = null;

/**
 * Register a getter for the in-memory CSRF token. Called from `api.ts` so we
 * don't duplicate the auth machinery — and to keep this file free of circular
 * imports with `@/services/api`.
 */
export function bindCsrfTokenSource(getter: () => string | null) {
  csrfTokenRef = { get value() { return getter(); } } as { value: string | null };
}

async function request<T>(
  endpoint: string,
  options: RequestInit = {},
): Promise<T> {
  const method = (options.method || 'GET').toUpperCase();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };
  if (
    method !== 'GET' &&
    method !== 'HEAD' &&
    csrfTokenRef &&
    csrfTokenRef.value
  ) {
    headers['x-csrf-token'] = csrfTokenRef.value;
  }
  const res = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    credentials: 'include',
    headers,
  });
  const text = await res.text();
  const body = text ? JSON.parse(text) : null;
  if (!res.ok) {
    throw new Error(body?.error?.message || `Request failed (${res.status})`);
  }
  return body?.data as T;
}

/** Encode a listKey for safe inclusion in a URL path segment. */
function encodeListKey(listKey: string): string {
  return encodeURIComponent(listKey);
}

export const preferences = {
  /** Fetch the saved views for one list. Returns `null` when none exist. */
  getSavedViews: (listKey: string) =>
    request<SavedViewList | null>(
      `/users/me/saved-views/${encodeListKey(listKey)}`,
    ),

  /** Replace the saved views for one list. */
  setSavedViews: (
    listKey: string,
    views: SavedView[],
    defaultViewId?: string | null,
  ) =>
    request<SavedViewList>(
      `/users/me/saved-views/${encodeListKey(listKey)}`,
      {
        method: 'PUT',
        body: JSON.stringify({ views, defaultViewId: defaultViewId ?? null }),
      },
    ),

  /**
   * Fetch the current user's preferences. `emailNotifications` is null when the
   * user has never changed it — resolve defaults with `shouldEmailForKind`
   * rather than assuming, so the UI agrees with the sender.
   *
   * `emailEnabled` is a server capability, not a preference: false means this
   * deployment cannot send email at all.
   */
  getMine: () =>
    request<{
      themeId: ThemeId | null;
      preferredLanguage: string;
      notificationSoundEnabled: boolean;
      emailNotifications: EmailNotificationPreferences | null;
      emailEnabled: boolean;
      asanaBannerDismissedAt: string | null;
    }>('/users/me/preferences'),

  dismissAsanaBanner: () =>
    request<{ dismissedAt: string }>('/users/me/asana-banner-dismissed', { method: 'PUT' }),

  /** Replace the current user's email-notification preferences. */
  setEmailNotifications: (prefs: EmailNotificationPreferences) =>
    request<EmailNotificationPreferences>('/users/me/preferences/email-notifications', {
      method: 'PUT',
      body: JSON.stringify(prefs),
    }),
};
